import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { SharedService } from '../../_services/shared.service';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { TranslatedToastService } from '../../_services/translated-toast.service';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { UserResDto } from '../../_dtos/userResDto';
import { EditResourceReqDto } from '../../_dtos/editResourceReqDto';
import { TeamsService } from '../../_services/teams.service';
import { SitesService } from '../../_services/sites.service';
import { BookingsService } from '../../_services/bookings.service';
import { GraphService } from '../../_services/graph.service';
import { ResourceTypes } from '../../_enums/resourceTypes';
import { TableDataService } from '../../_services/table-data.service';
import { ResourceStatus } from '../../_enums/resourceStatus';
import { ValidatorService } from '../../_services/validator.service';
import { EditFormType } from './editFormType';

@Component({
  selector: 'app-lifecycle-form',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    CommonModule,
    MatTooltip,
    TranslateModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatError,
  ],
  standalone: true,
  templateUrl: './edit-resource-form.component.html',
  styleUrl: './edit-resource-form.component.css',
})
export class EditResourceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public editResourceForm!: FormGroup;
  public displayName!: string;
  public primaryOwner!: string;
  public secondaryOwner!: string;
  private isGroupConnected!: boolean;
  private connectedGroupId!: string;
  private id!: string;
  private resourceType!: ResourceTypes;
  primaryOwnerControl = new FormControl<UserResDto | null>(null);
  secondaryOwnerControl = new FormControl<UserResDto | null>(null);
  filteredPrimaryOwner?: Observable<UserResDto[]>;
  filteredSecondaryOwner?: Observable<UserResDto[]>;

  constructor(
    private toastr: TranslatedToastService,
    private sharedService: SharedService,
    private tableDataService: TableDataService,
    private teamsService: TeamsService,
    private sitesService: SitesService,
    private bookingsService: BookingsService,
    private graphService: GraphService,
    private validatorService: ValidatorService,
  ) {}

  async ngOnInit() {
    this.getFormData();
  }

  displayUserFn(user: UserResDto | null): string {
    return user ? `${user?.displayName}` : '';
  }

  async onSubmit() {
    if (this.editResourceForm.valid) {
      const formValues: EditFormType = this.editResourceForm.getRawValue();

      const primaryOwnerUpn = formValues.primaryOwner?.userPrincipalName;
      const secondaryOwnerUpn = formValues.secondaryOwner?.userPrincipalName;

      const editResourceReqDto: EditResourceReqDto = {
        primaryOwner: formValues.primaryOwner.userPrincipalName,
        secondaryOwner: formValues.secondaryOwner?.userPrincipalName,
      };

      if (this.resourceType === ResourceTypes.TEAM) {
        this.teamsService.updateTeams(this.id, editResourceReqDto).subscribe({
          next: async () => {
            await this.toastr.show(
              'success',
              'TOASTS.RESOURCE.UPDATED_MESSAGE',
              'TOASTS.RESOURCE.UPDATED_TITLE',
            );
            this.notifyOwnersUpdated(primaryOwnerUpn, secondaryOwnerUpn);
          },
        });
      }
      if (this.resourceType === ResourceTypes.SHAREPOINT) {
        this.sitesService
          .updateSharepoint(this.id, editResourceReqDto)
          .subscribe({
            next: async () => {
              await this.toastr.show(
                'success',
                'TOASTS.RESOURCE.UPDATED_MESSAGE',
                'TOASTS.RESOURCE.UPDATED_TITLE',
              );
              this.notifyOwnersUpdated(primaryOwnerUpn, secondaryOwnerUpn);
            },
          });
      }
      if (this.resourceType === ResourceTypes.BOOKINGS) {
        this.bookingsService
          .updateBooking(this.id, editResourceReqDto)
          .subscribe({
            next: async () => {
              await this.toastr.show(
                'success',
                'TOASTS.RESOURCE.UPDATED_MESSAGE',
                'TOASTS.RESOURCE.UPDATED_TITLE',
              );
              this.notifyOwnersUpdated(primaryOwnerUpn, secondaryOwnerUpn);
            },
          });
      }

      this.sharedService.closeModal();
    }
  }

  // The new secondary owner, or null when none was selected; without one the resource is in warning state
  private notifyOwnersUpdated(
    primaryOwnerUpn: string,
    secondaryOwnerUpn: string | undefined,
  ) {
    const hasSecondaryOwner = !!secondaryOwnerUpn?.trim();
    this.tableDataService.notifyResourceUpdate(this.id, {
      primaryOwner: primaryOwnerUpn,
      secondaryOwner: hasSecondaryOwner ? secondaryOwnerUpn! : null,
      resourceStatus: hasSecondaryOwner
        ? ResourceStatus.ACTIVE
        : ResourceStatus.WARNING,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm() {
    this.editResourceForm = new FormGroup<any>({
      name: new FormControl(
        { value: this.displayName, disabled: true },
        { nonNullable: true },
      ),
      primaryOwner: new FormControl<UserResDto | null>(null, {
        nonNullable: false,
        validators: [
          Validators.required,
          this.validatorService.userObjectValidator(),
        ],
      }),
      secondaryOwner: new FormControl<UserResDto | null>(null, {
        nonNullable: false,
        validators: [this.validatorService.userObjectValidator()],
      }),
    });

    this.primaryOwnerControl = this.editResourceForm.get(
      'primaryOwner',
    ) as FormControl<UserResDto | null>;
    this.secondaryOwnerControl = this.editResourceForm.get(
      'secondaryOwner',
    ) as FormControl<UserResDto | null>;

    // Bookings adds new owners as staff members: they need a BFH account with a mailbox
    if (this.resourceType === ResourceTypes.BOOKINGS) {
      const currentOwners = () => [this.primaryOwner, this.secondaryOwner];
      for (const control of [this.primaryOwnerControl, this.secondaryOwnerControl]) {
        control.addValidators(this.validatorService.bfhUpnValidator());
        control.addAsyncValidators(
          this.validatorService.mailboxValidator(currentOwners),
        );
      }
    }
  }

  ownerErrorMessage(control: FormControl, fallback: string): string {
    if (control.hasError('notBfhUser')) return 'BOOKINGS_OWNER_NOT_BFH_USER';
    if (control.hasError('noMailbox')) return 'BOOKINGS_OWNER_NO_MAILBOX';
    if (control.hasError('mailboxCheckFailed'))
      return 'BOOKINGS_OWNER_CHECK_FAILED';
    return fallback;
  }

  private setupUserSearch() {
    this.setupUserSearchAutocomplete(
      this.primaryOwnerControl,
      this.secondaryOwnerControl,
      (value) => (this.filteredPrimaryOwner = value),
      this.isGroupConnected ? this.connectedGroupId : this.id,
      this.resourceType,
    );

    this.setupUserSearchAutocomplete(
      this.secondaryOwnerControl,
      this.primaryOwnerControl,
      (value) => (this.filteredSecondaryOwner = value),
      this.isGroupConnected ? this.connectedGroupId : this.id,
      this.resourceType,
    );
  }

  private getFormData() {
    this.sharedService.editForm$.subscribe((value) => {
      if (!value) return;

      this.displayName = value.displayName ?? '';
      this.primaryOwner = value.primaryOwner ?? '';
      this.secondaryOwner = value.secondaryOwner ?? '';
      this.id = value.id ?? '';
      this.resourceType = value.resourceType ?? '';
      this.isGroupConnected = value.isGroupConnected ?? false;
      this.connectedGroupId = value.connectedGroupId ?? '';

      this.initializeForm();

      if (this.primaryOwner && this.primaryOwner.trim() !== '') {
        this.graphService.getUserByUpn(this.primaryOwner).subscribe((users) => {
          if (users && users.length > 0 && this.primaryOwnerControl) {
            this.primaryOwnerControl.setValue(users[0]);
          }
        });
      }

      if (this.secondaryOwner && this.secondaryOwner.trim() !== '') {
        this.graphService
          .getUserByUpn(this.secondaryOwner)
          .subscribe((users) => {
            if (users && users.length > 0 && this.secondaryOwnerControl) {
              this.secondaryOwnerControl.setValue(users[0]);
            }
          });
      }

      this.setupUserSearch();
    });
  }

  onPrimaryOwnerSelected(event: MatAutocompleteSelectedEvent) {
    const selectedUser = event.option.value as UserResDto;
    this.primaryOwnerControl.setValue(selectedUser);
  }

  onSecondaryOwnerSelected(event: MatAutocompleteSelectedEvent) {
    const selectedUser = event.option.value as UserResDto;
    this.secondaryOwnerControl.setValue(selectedUser);
  }

  private setupUserSearchAutocomplete(
    firstControl: FormControl<UserResDto | null | string>,
    secondControl: FormControl<UserResDto | null | string>,
    assignTo: (selectedOwners: Observable<UserResDto[]>) => void,
    id: string,
    resourceType: ResourceTypes,
  ) {
    const filteredOwners = combineLatest([
      firstControl.valueChanges.pipe(
        startWith(firstControl.value || ''),
        debounceTime(300),
        distinctUntilChanged(),
      ),
      secondControl.valueChanges.pipe(startWith(secondControl.value || null)),
    ]).pipe(
      switchMap(([searchValue, removeValue]) => {
        // Handle both string and UserResDto
        const searchTerm =
          typeof searchValue === 'string'
            ? searchValue.trim()
            : searchValue?.displayName || '';

        if (!searchTerm || searchTerm === '') {
          return of([]);
        }

        let search$: Observable<UserResDto[]>;
        if (resourceType === ResourceTypes.SHAREPOINT) {
          if (this.isGroupConnected)
            search$ = this.graphService.searchGroupOwner(searchTerm, id);
          else search$ = this.sitesService.searchSiteOwner(searchTerm, id);
        } else if (resourceType === ResourceTypes.TEAM)
          search$ = this.graphService.searchGroupOwner(searchTerm, id);
        else search$ = this.graphService.searchPeople(searchTerm);

        return search$.pipe(
          map((people) => {
            if (!removeValue) return people;

            // Handle filtering based on removeValue type
            const removeUpn =
              typeof removeValue === 'string'
                ? removeValue
                : removeValue?.userPrincipalName;

            return people.filter(
              (user) => user.userPrincipalName !== removeUpn,
            );
          }),
        );
      }),
    );
    assignTo(filteredOwners);
  }
}
